import { regexes } from "zod";
import { diagnoseEmailDomain, type EmailDomainProblem } from "./email-domain";

const SUGGESTION_PREFIX = "Did you mean ";
const SUGGESTION_SUFFIX = "?";

export const EMAIL_FORMAT = {
  pattern: regexes.html5Email,
  maxLength: 254,

  validate(value: string): boolean {
    if (!value) return false;
    const trimmed = value.trim();
    const [local, domain] = trimmed.split("@");
    return (
      trimmed.length <= this.maxLength &&
      this.pattern.test(trimmed) &&
      !!domain?.includes(".") &&
      !local?.startsWith(".") &&
      !local?.endsWith(".") &&
      !local?.includes("..")
    );
  },

  normalize(value: string | undefined | null): string | undefined {
    if (!value) return undefined;
    const trimmed = value.trim().toLowerCase();
    return this.validate(trimmed) ? trimmed : undefined;
  },

  diagnose(
    value: string,
    options: { rejectReserved: boolean },
  ): EmailDomainProblem | undefined {
    return this.validate(value) ? diagnoseEmailDomain(value, options) : undefined;
  },

  suggest(value: string): string | undefined {
    const problem = this.diagnose(value, { rejectReserved: false });
    return problem?.kind === "typo" ? problem.suggestion : undefined;
  },

  problemMessage(problem: EmailDomainProblem): string {
    return problem.kind === "typo"
      ? emailSuggestionMessage(problem.suggestion)
      : this.reservedMessage;
  },

  errorMessage: "Enter a valid email address",
  reservedMessage: "Enter an email address that can receive mail",
} as const;

export function emailSuggestionMessage(suggestion: string): string {
  return `${SUGGESTION_PREFIX}${suggestion}${SUGGESTION_SUFFIX}`;
}

export function isEmailSuggestionMessage(message: string): boolean {
  return message.startsWith(SUGGESTION_PREFIX) && message.endsWith(SUGGESTION_SUFFIX);
}

export function canonicalEmail(
  value: string | undefined | null,
): string | undefined {
  if (!value) return undefined;
  return EMAIL_FORMAT.normalize(value) ?? (value.trim() || undefined);
}
